<template>
  <TContainer class="mt-8 md:mt-12 relative">
    <TOverlay
      :show="[STATES.CREATING, STATES.GETTING_PRICES].some(state.matches)"
      class="z-20"
    />

    <div class="clear-both">
      <div class="float-left w-full lg:w-2/3 lg:pr-2">
        <div class="">
          <ApiResponseError
            v-if="[STATES.CREATE_ERROR].some(state.matches)"
            :api-response-error="formError"
            class="mb-8"
          />

          <template
            v-if="
              [
                STATES.LOAD_SUCCESS,
                STATES.CREATING,
                STATES.GETTING_PRICES,
                STATES.CREATE_ERROR,
              ].some(state.matches)
            "
          >
            <div v-if="cottageContactWhatsAppPage">
              <TText tag="h1" size="lg" variant="semibold" classes="mb-2">
                {{
                  t('views.cottage-contact-whatsapp.title', {
                    name: cottageContactWhatsAppPage.name,
                  })
                }}
              </TText>
              <TText
                v-if="
                  cottageContactWhatsAppPage.owner.answer &&
                  cottageContactWhatsAppPage.owner.answer.rate !== null
                "
              >
                {{
                  cottageContactWhatsAppPage.owner.answer.rate > 0
                    ? t('views.cottage-contact-whatsapp.answerRate', {
                        rate: cottageContactWhatsAppPage.owner.answer.rate,
                        range: t(
                          `range.${cottageContactWhatsAppPage.owner.answer.range}`,
                        ),
                        value: cottageContactWhatsAppPage.owner.answer.value,
                        unit: t(
                          `time.${cottageContactWhatsAppPage.owner.answer.unit}`,
                          cottageContactWhatsAppPage.owner.answer.value,
                        ),
                      })
                    : t('views.cottage-contact-whatsapp.noAnswerRate')
                }}
              </TText>

              <TSeparator class="mt-6 mb-8" />
            </div>
          </template>
        </div>
      </div>
      <div class="float-right w-full lg:w-1/3 lg:pl-2">
        <div class="">
          <template
            v-if="
              [
                STATES.LOAD_SUCCESS,
                STATES.CREATING,
                STATES.GETTING_PRICES,
                STATES.CREATE_ERROR,
              ].some(state.matches)
            "
          >
            <div v-if="cottageContactWhatsAppPage">
              <UCard :ui="publicCard">
                <TText variant="semibold" classes="mb-2">
                  {{ t('views.cottage-contact-whatsapp.price.message') }}
                </TText>
                <CottageContactWhatsAppPrice
                  :item="cottageContactWhatsAppPage"
                  @submit="onPriceSubmit"
                  @update-dates="onPriceUpdateDates"
                  @update-people="onPriceUpdatePeople"
                />

                <CottageContactWhatsAppPriceResponse
                  v-if="pricesResponse"
                  :item="pricesResponse"
                />
              </UCard>
            </div>
          </template>
        </div>
      </div>
      <div class="float-left w-full lg:w-2/3 lg:pr-2 mb-20">
        <div class="">
          <template
            v-if="
              [
                STATES.LOAD_SUCCESS,
                STATES.CREATING,
                STATES.GETTING_PRICES,
                STATES.CREATE_ERROR,
              ].some(state.matches)
            "
          >
            <div v-if="cottageContactWhatsAppPage">
              <CottageContactWhatsAppForm
                :item="cottageContactWhatsAppPage"
                @submit="onSubmit"
              />
              <TText size="sm" color="gray600" classes="mt-4">
                {{ t('views.cottage-contact-whatsapp.underFormMessage') }}
              </TText>
            </div>
          </template>

          <CottageContactWhatsAppPageSuccess
            v-if="
              state.matches(STATES.CREATE_SUCCESS) &&
              cottageContactWhatsAppPage &&
              onSuccessFormData
            "
            :id="cottageContactWhatsAppPage.id"
            :name="cottageContactWhatsAppPage.name"
            :form-data="onSuccessFormData"
          />
        </div>
      </div>
    </div>
  </TContainer>
</template>

<script setup lang="ts">
// State Machine
import { useMachine } from '@xstate/vue'
import fsm, {
  STATES,
  TRANSITIONS,
} from '../utils/stateMachines/CottageContactWhatsAppPageForm.fsm'

// Composables
import { useCottageContactWhatsAppPage } from '../composables/useCottageContactWhatsAppPage'
import { useCottageContactWhatsAppPagePrices } from '../composables/useCottageContactWhatsAppPagePrices'
import {
  sendGa4ContactEvent,
  sendCottageConversionAll,
} from '~/modules/integrations/ga4/composables/useAnalyticsGa4Composable'
import { sendAdsContact } from '~/modules/integrations/ads/composables/useAnalyticsAdsComposable'
import { sendCottageContact } from '~/modules/integrations/hotjar/composables/useHotjarComposable'

// Entities
import type { CottageContactWhatsAppPage } from '../entities/CottageContactWhatsAppPage'
import type {
  CottageContactWhatsAppPageCreateForm,
  CottageContactWhatsAppPageCreateResponse,
} from '../entities/CottageContactWhatsAppPageCreate'
import type {
  CottageContactWhatsAppPagePricesForm,
  CottageContactWhatsAppPagePricesDates,
  CottageContactWhatsAppPagePricesPeople,
  CottageContactWhatsAppPagePricesResponse,
} from '../entities/CottageContactWhatsAppPagePrices'

// Design System
import { publicCard } from '~/modules/design-system/utils/nuxt-ui-config/components/card'

// Composables
const { sendFormData } = useCottageContactWhatsAppPage()
const { getPricesData } = useCottageContactWhatsAppPagePrices()
const { setCookieRuralityCottageContact } = useCookiesRurality()
const { state, send } = useMachine(fsm)

// Props
interface propsInterface {
  cottageContactWhatsAppPage: CottageContactWhatsAppPage
}

const props = defineProps<propsInterface>()

// Constants
let formError: ApiResponseError | null = null
const dates = ref<CottageContactWhatsAppPagePricesDates | null>(null)
const people = ref<CottageContactWhatsAppPagePricesPeople | null>(null)

const onSuccessFormData = ref<CottageContactWhatsAppPageCreateForm | null>(null)

const pricesResponse = ref<CottageContactWhatsAppPagePricesResponse | null>(
  null,
)

// Methods
const onPriceSubmit = async (ev: CottageContactWhatsAppPagePricesForm) => {
  send(TRANSITIONS.onGetPrices)
  if (props.cottageContactWhatsAppPage) {
    const responseGetPricesData = await getPricesData({
      CottageContactWhatsAppPage: props.cottageContactWhatsAppPage,
      dates: ev.dates,
      people: ev.people,
    })
    pricesResponse.value = responseGetPricesData
  }
  send(TRANSITIONS.onGetPricesResult)
}

const onPriceUpdateDates = async (
  ev: CottageContactWhatsAppPagePricesDates,
) => {
  dates.value = ev
}

const onPriceUpdatePeople = async (
  ev: CottageContactWhatsAppPagePricesPeople,
) => {
  people.value = ev
}

const onSubmit = async (ev: CottageContactWhatsAppPageCreateForm) => {
  send(TRANSITIONS.onCreate)
  if (props.cottageContactWhatsAppPage) {
    const responseSendFormData = await sendFormData({
      createForm: ev,
      CottageContactWhatsAppPage: props.cottageContactWhatsAppPage,
      dates: dates.value,
      people: people.value,
    })
    if (responseSendFormData.apiResponseError) {
      formError = responseSendFormData.apiResponseError
      send(TRANSITIONS.onCreateError)
    } else if (responseSendFormData.apiResponseSuccess) {
      onSuccess(responseSendFormData.apiResponseSuccess, ev)
    }
  }
}

const onSuccess = (
  CottageContactWhatsAppPageCreateResponse: CottageContactWhatsAppPageCreateResponse,
  formData: CottageContactWhatsAppPageCreateForm,
) => {
  if (props.cottageContactWhatsAppPage) {
    setDataRepository(formData)
    onSuccessFormData.value = formData

    sendGa4ContactEvent({
      province: props.cottageContactWhatsAppPage.province.name,
      method: 'whatsapp',
    })
    sendCottageConversionAll()
    sendCottageContact()
    if (CottageContactWhatsAppPageCreateResponse) {
      sendAdsContact({
        email: CottageContactWhatsAppPageCreateResponse.traveler.email,
        phone: CottageContactWhatsAppPageCreateResponse.traveler.phone,
      })
    }

    send(TRANSITIONS.onCreateSuccess)
  }
}

const setDataRepository = (formData: CottageContactWhatsAppPageCreateForm) => {
  setCookieRuralityCottageContact({
    message: formData.message,
    traveler_email: formData.email,
    traveler_name: formData.name,
    traveler_phone: formData.phone,
  })
}
</script>
